/**
 * ADMIN-T011 — Bootstrap state machine per SPEC §7.1.
 *
 * States and transitions:
 *   Idle → Booting (start)
 *   Booting → ConfigLoading → AuthResolving → PermissionPriming → Ready
 *   Any → BootstrapFailed (on terminal error)
 *   Ready → Reauthenticating (on 401) → AuthResolving → … → Ready
 *
 * `BootstrapFailed` is terminal until user retries. `Reauthenticating` is non-terminal
 * (Ready state can resume once permissions reconcile).
 */

import { reactive, readonly } from 'vue';
import { ERROR_CODES, type ErrorCode } from '@/observability/errorCodes';

export type BootstrapState =
  | 'Idle'
  | 'Booting'
  | 'ConfigLoading'
  | 'AuthResolving'
  | 'PermissionPriming'
  | 'Ready'
  | 'Reauthenticating'
  | 'BootstrapFailed';

export interface BootstrapStore {
  state: BootstrapState;
  error?: { code: ErrorCode; message: string; cause?: unknown };
  startedAt?: number;
  readyAt?: number;
}

const ALLOWED: Record<BootstrapState, BootstrapState[]> = {
  Idle: ['Booting', 'BootstrapFailed'],
  Booting: ['ConfigLoading', 'BootstrapFailed'],
  ConfigLoading: ['AuthResolving', 'BootstrapFailed'],
  AuthResolving: ['PermissionPriming', 'BootstrapFailed', 'Reauthenticating'],
  PermissionPriming: ['Ready', 'BootstrapFailed'],
  Ready: ['Reauthenticating'],
  Reauthenticating: ['AuthResolving', 'BootstrapFailed', 'Ready'],
  BootstrapFailed: ['Booting'],
};

const store = reactive<BootstrapStore>({ state: 'Idle' });

const listeners = new Set<(s: BootstrapStore) => void>();

export function bootstrapState(): Readonly<BootstrapStore> {
  return readonly(store) as Readonly<BootstrapStore>;
}

export function useBootstrapState(): Readonly<BootstrapStore> {
  return bootstrapState();
}

export function onBootstrapChange(fn: (s: BootstrapStore) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export class IllegalBootstrapTransition extends Error {
  override readonly name = 'IllegalBootstrapTransition';
  constructor(from: BootstrapState, to: BootstrapState) {
    super(`illegal bootstrap transition: ${from} → ${to}`);
  }
}

export function transition(next: BootstrapState, error?: BootstrapStore['error']): void {
  const cur = store.state;
  if (!ALLOWED[cur].includes(next)) {
    throw new IllegalBootstrapTransition(cur, next);
  }
  store.state = next;
  if (next === 'Booting') store.startedAt = Date.now();
  if (next === 'Ready') store.readyAt = Date.now();
  if (next === 'BootstrapFailed') {
    store.error = error ?? {
      code: ERROR_CODES.bootstrap_failed,
      message: 'unknown bootstrap failure',
    };
  } else {
    store.error = undefined;
  }
  for (const l of listeners) l(store);
}

export function resetBootstrap(): void {
  store.state = 'Idle';
  store.error = undefined;
  store.startedAt = undefined;
  store.readyAt = undefined;
}

export interface BootstrapDeps {
  loadRuntimeConfig: () => Promise<unknown>;
  resolveSession: () => Promise<{ authenticated: boolean }>;
  primePermissions: () => Promise<void>;
}

/**
 * Run the bootstrap pipeline end-to-end. The dependencies are passed in so this function
 * is fully unit-testable without touching globals (per ADMIN-T011 acceptance).
 */
export async function runBootstrap(deps: BootstrapDeps): Promise<void> {
  try {
    transition('Booting');
    transition('ConfigLoading');
    await deps.loadRuntimeConfig();

    transition('AuthResolving');
    const session = await deps.resolveSession();
    if (!session.authenticated) {
      // Stays in AuthResolving — the login route will trigger the next step.
      return;
    }

    transition('PermissionPriming');
    await deps.primePermissions();

    transition('Ready');
  } catch (err) {
    const code: ErrorCode = (err as { code?: ErrorCode })?.code ?? ERROR_CODES.bootstrap_failed;
    transition('BootstrapFailed', {
      code,
      message: err instanceof Error ? err.message : String(err),
      cause: err,
    });
  }
}

export function triggerReauth(): void {
  if (store.state === 'Ready' || store.state === 'AuthResolving') {
    transition('Reauthenticating');
  }
}
